import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useErrorMessage } from "../../../lib/api-errors";
import { isPlanGateError } from "../shared/plan-gate";
import type { ChannelOut, ChannelType } from "../shared/types";
import type { ChannelSwitch } from "./channel-card";
import { useToggleChannel } from "./use-channels";

/** Page-level facts every channel card needs. */
export interface ChannelPageContext {
  wsSlug: string;
  wsId: string;
  agentId: string;
  canManage: boolean;
  /** Tooltip when `canManage` is false (no permission / read-only support session). */
  lockedReason: string | undefined;
  /** Called when an action answers 402 (renders the upgrade card). */
  onPlanGate: (type: ChannelType) => void;
}

/** Shared error handling: 402 → upgrade card, anything else → friendly toast. */
export function useChannelErrorHandler(ctx: ChannelPageContext) {
  const message = useErrorMessage();
  return (type: ChannelType) => (error: unknown) => {
    if (isPlanGateError(error)) ctx.onPlanGate(type);
    else toast.error(message(error));
  };
}

/** Enable switch wired to `PATCH /v1/agents/{id}/channels/{type}` with success toasts. */
export function useChannelSwitch(ctx: ChannelPageContext) {
  const { t } = useTranslation();
  const toggle = useToggleChannel(ctx.agentId);
  const onError = useChannelErrorHandler(ctx);
  return (channel: ChannelOut, override?: Partial<ChannelSwitch>): ChannelSwitch => {
    const name = t(`deployKnowledge.channels.names.${channel.type}`);
    const pending = toggle.isPending && toggle.variables.type === channel.type;
    return {
      checked: pending ? toggle.variables.enabled : channel.enabled,
      disabled: !ctx.canManage || pending,
      reason: ctx.canManage ? undefined : ctx.lockedReason,
      onChange: (enabled) => {
        toggle.mutate(
          { type: channel.type, enabled },
          {
            onSuccess: () => toast.success(t(enabled ? "deployKnowledge.channels.toggledOn" : "deployKnowledge.channels.toggledOff", { channel: name })),
            onError: onError(channel.type),
          },
        );
      },
      ...override,
    };
  };
}
